import React, { useEffect, useState } from 'react';
import { Card } from '@playground/ui';
import { apiFetch, apiFetchWithToken } from '@/platform/api.js';

interface GameOption {
  id: string;
  name: string;
}

interface FrontendSettings {
  siteName: string;
  heroGameId: string;
}

export function Frontend({ token }: { token: string }) {
  const [settings, setSettings] = useState<FrontendSettings>({ siteName: '', heroGameId: '' });
  const [games, setGames] = useState<GameOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Load current config
    apiFetch('/api/admin/config', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<{ settings?: { siteName?: string; heroGameId?: string } }>;
      })
      .then((d) => {
        setSettings({
          siteName: d.settings?.siteName ?? '',
          heroGameId: d.settings?.heroGameId ?? '',
        });
      })
      .catch(() => setError('Failed to load config.'));

    // Load game list for the hero picker.
    // `setGames(undefined)` on a malformed response used to crash the render.
    apiFetchWithToken('/api/games', token)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { games?: GameOption[] } | null) => setGames(d?.games ?? []))
      .catch(() => {});
  }, [token]);

  async function save() {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const configRes = await apiFetch('/api/admin/config', { headers: { Authorization: `Bearer ${token}` } });
      if (!configRes.ok) throw new Error(`Could not read current config (${configRes.status})`);
      const config = await configRes.json() as Record<string, unknown>;
      const existingSettings = (config['settings'] as Record<string, unknown>) ?? {};

      // `apiFetch` resolves for 4xx/5xx too — without this check a rejected
      // save still rendered "✓ Saved".
      const putRes = await apiFetch('/api/admin/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...config,
          settings: {
            ...existingSettings,
            siteName: settings.siteName,
            heroGameId: settings.heroGameId,
          },
        }),
      });
      if (!putRes.ok) throw new Error(`Save rejected (${putRes.status})`);

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4">Frontend Settings</h2>

        <div className="space-y-5">
          {/* Site name */}
          <div>
            <label htmlFor="admin-site-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Site name
            </label>
            <input
              id="admin-site-name"
              type="text"
              value={settings.siteName}
              onChange={(e) => setSettings((s) => ({ ...s, siteName: e.target.value }))}
              placeholder="Playground"
              maxLength={40}
              aria-describedby="admin-site-name-hint"
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            />
            <p id="admin-site-name-hint" className="mt-1 text-xs text-gray-500 dark:text-gray-400">Shown in the top bar and browser title.</p>
          </div>

          {/* Hero game */}
          <div>
            <label htmlFor="admin-hero-game" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Hero game
            </label>
            <select
              id="admin-hero-game"
              value={settings.heroGameId}
              onChange={(e) => setSettings((s) => ({ ...s, heroGameId: e.target.value }))}
              aria-describedby="admin-hero-game-hint"
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <option value="">— First enabled game (default) —</option>
              {games.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
            <p id="admin-hero-game-hint" className="mt-1 text-xs text-gray-500 dark:text-gray-400">The game featured in the large hero section on the home page.</p>
          </div>

          {/* Save */}
          {error && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving}
            className="rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 px-4 py-2 text-sm font-semibold text-white transition-colors"
          >
            {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save'}
          </button>
        </div>
      </Card>
    </div>
  );
}
